import type { AppState, Child, ExchangeRecord, KnowledgeItem, LearningPlan, LearningPlansByChild, LearningProgress, PoemSong, ReviewEvent } from '../types';
import { createSeedState, knowledgeIdentityKeys, legacyDemoChildIds } from '../data/seed';

const LOCAL_KEY = 'memo-growing-state-v1';
const PLAN_MIGRATION_VERSION = 2;
let remoteTimer: ReturnType<typeof setTimeout> | undefined;

function uniquePlanIds(value: unknown): LearningPlan {
  return Array.isArray(value)
    ? [...new Set(value.filter((itemId): itemId is string => typeof itemId === 'string'))]
    : [];
}

function unionPlanIds(plans: LearningPlansByChild): LearningPlan {
  return [...new Set(Object.values(plans).flat())];
}

export function readLocalState(): AppState | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    return normalizeState(JSON.parse(raw) as AppState);
  } catch {
    return null;
  }
}

export function writeLocalState(state: AppState) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(state));
}

function normalizeState(state: AppState): AppState {
  const seed = createSeedState();
  const hiddenKnowledgeIds = state.hiddenKnowledgeIds ?? [];
  const hidden = new Set(hiddenKnowledgeIds);
  const dedupedKnowledge: KnowledgeItem[] = [];
  const existingIds = new Set<string>();
  const existingKeys = new Set<string>();
  for (const item of state.knowledge) {
    const keys = knowledgeIdentityKeys(item);
    if (existingIds.has(item.id) || keys.some((key) => existingKeys.has(key))) continue;
    existingIds.add(item.id);
    keys.forEach((key) => existingKeys.add(key));
    dedupedKnowledge.push(item);
  }
  const missingKnowledge = seed.knowledge.filter((item) => {
    const keys = knowledgeIdentityKeys(item);
    const missing = !hidden.has(item.id)
      && !existingIds.has(item.id)
      && !keys.some((key) => existingKeys.has(key));
    if (missing) {
      existingIds.add(item.id);
      keys.forEach((key) => existingKeys.add(key));
    }
    return missing;
  });
  const mergedKnowledge = [...dedupedKnowledge, ...missingKnowledge];
  const knowledgeIds = new Set(mergedKnowledge.map((item) => item.id));
  const storedState = state as AppState & { learningPlans?: unknown };
  const legacyPlans = storedState.learningPlans;
  const legacyPlanIds: string[] = Array.isArray(storedState.learningPlan)
    ? storedState.learningPlan
    : Array.isArray(legacyPlans)
      ? legacyPlans
      : legacyPlans && typeof legacyPlans === 'object'
        ? Object.values(legacyPlans).flatMap((ids) => Array.isArray(ids) ? ids : [])
        : state.progress.map((entry) => entry.itemId);
  const baseState = { ...state };
  delete (baseState as AppState & { learningPlans?: unknown }).learningPlans;
  const legacyChildIds = new Set<string>(legacyDemoChildIds);
  const children = (state.children ?? []).filter((child) => !legacyChildIds.has(child.id));
  const progress = (state.progress ?? []).filter((entry) => !legacyChildIds.has(entry.childId));
  const knowledgeById = new Map(mergedKnowledge.map((item) => [item.id, item]));
  const progressItemIds = new Set(progress.map((entry) => entry.itemId));
  const requestedPlanMigrationVersion = Math.max(0, Number(state.planMigrationVersion) || 0);
  const planIds = [...new Set(legacyPlanIds)].filter((itemId) => knowledgeIds.has(itemId));
  const cleanedPlanIds = requestedPlanMigrationVersion >= 1
    ? planIds
    : planIds.filter((itemId) => {
      const item = knowledgeById.get(itemId);
      return item?.type !== 'character' || progressItemIds.has(itemId);
    });
  const storedPlansByChild = state.learningPlansByChild;
  const hasPerChildPlans = requestedPlanMigrationVersion >= 2
    && Boolean(storedPlansByChild && typeof storedPlansByChild === 'object');
  const learningPlansByChild: LearningPlansByChild = Object.fromEntries(children.map((child) => [
    child.id,
    hasPerChildPlans
      ? uniquePlanIds(storedPlansByChild?.[child.id]).filter((itemId) => knowledgeIds.has(itemId))
      : [...cleanedPlanIds]
  ]));
  const learningPlan = unionPlanIds(learningPlansByChild);
  const exchanges = (state.exchanges ?? []).filter((record) => !legacyChildIds.has(record.childId));
  const activeChildId = state.activeChildId && !legacyChildIds.has(state.activeChildId)
    ? state.activeChildId
    : children[0]?.id ?? null;
  return {
    ...seed,
    ...baseState,
    version: Math.max(seed.version, state.version || 1),
    planMigrationVersion: PLAN_MIGRATION_VERSION,
    children,
    progress,
    products: state.products ?? seed.products,
    exchanges,
    activeChildId,
    knowledge: mergedKnowledge,
    learningPlansByChild,
    learningPlan,
    hiddenKnowledgeIds,
    settings: {
      ...seed.settings,
      ...state.settings,
      intervals: {
        ...seed.settings.intervals,
        ...state.settings?.intervals
      },
      rewards: {
        ...seed.settings.rewards,
        ...state.settings?.rewards
      }
    }
  };
}

function mergeChildren(states: AppState[]): Child[] {
  const children = new Map<string, Child>();
  for (const state of states) {
    for (const child of state.children) {
      const existing = children.get(child.id);
      children.set(child.id, existing
        ? { ...existing, ...child, stars: Math.max(existing.stars, child.stars) }
        : child);
    }
  }
  return [...children.values()];
}

function mergeLearningPlansByChild(states: AppState[], children: Child[]): LearningPlansByChild {
  return Object.fromEntries(children.map((child) => [
    child.id,
    [...new Set(states.flatMap((state) => state.learningPlansByChild?.[child.id] ?? []))]
  ]));
}

function mergeReviewHistory(entries: LearningProgress[]): ReviewEvent[] {
  const events = new Map<string, ReviewEvent>();
  for (const entry of entries) {
    for (const event of entry.history ?? []) {
      const key = event.id || `${event.childId}:${event.itemId}:${event.reviewedAt}:${event.result}`;
      events.set(key, event);
    }
  }
  return [...events.values()]
    .sort((left, right) => new Date(right.reviewedAt).getTime() - new Date(left.reviewedAt).getTime())
    .slice(0, 200);
}

function mergeProgress(states: AppState[]): LearningProgress[] {
  const grouped = new Map<string, LearningProgress[]>();
  for (const state of states) {
    for (const entry of state.progress) {
      const key = `${entry.childId}:${entry.itemId}`;
      const group = grouped.get(key) ?? [];
      group.push(entry);
      grouped.set(key, group);
    }
  }

  return [...grouped.values()].map((entries) => {
    const history = mergeReviewHistory(entries);
    const latestEvent = history[0];
    const latestEntry = [...entries].sort((left, right) => {
      const leftTime = left.history?.[0]?.reviewedAt || left.learnedAt;
      const rightTime = right.history?.[0]?.reviewedAt || right.learnedAt;
      return new Date(rightTime).getTime() - new Date(leftTime).getTime();
    })[0];
    const masteredAt = entries
      .map((entry) => entry.masteredAt)
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];
    return {
      ...latestEntry,
      id: latestEntry.id,
      stage: latestEvent?.stageAfter ?? Math.max(...entries.map((entry) => entry.stage), 0),
      learnedAt: entries.map((entry) => entry.learnedAt).sort()[0],
      masteredAt: latestEvent?.completed ? latestEvent.reviewedAt : masteredAt,
      nextReviewAt: latestEntry.nextReviewAt,
      attempts: Math.max(history.length, ...entries.map((entry) => entry.attempts)),
      correctCount: Math.max(
        history.filter((event) => event.result === 'known').length,
        ...entries.map((entry) => entry.correctCount)
      ),
      forgotCount: Math.max(
        history.filter((event) => event.result === 'forgot').length,
        ...entries.map((entry) => entry.forgotCount)
      ),
      history
    };
  });
}

function mergeProducts(states: AppState[]) {
  const products = new Map(states[0].products.map((product) => [product.id, product]));
  for (const state of states.slice(1)) {
    for (const product of state.products) products.set(product.id, { ...products.get(product.id), ...product });
  }
  return [...products.values()];
}

function mergeExchanges(states: AppState[]): ExchangeRecord[] {
  const exchanges = new Map<string, ExchangeRecord>();
  for (const state of states) {
    for (const record of state.exchanges) exchanges.set(record.id, record);
  }
  return [...exchanges.values()].sort((left, right) => new Date(right.exchangedAt).getTime() - new Date(left.exchangedAt).getTime());
}

export function mergeStates(states: Array<AppState | null | undefined>): AppState {
  const valid = states
    .map((state) => state ? normalizeState(state) : null)
    .filter((state): state is AppState => Boolean(state))
    .sort((left, right) => new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime());
  if (!valid.length) return createSeedState();
  const latest = valid[valid.length - 1];
  const children = mergeChildren(valid);
  const learningPlansByChild = mergeLearningPlansByChild(valid, children);
  return normalizeState({
    ...latest,
    children,
    progress: mergeProgress(valid),
    learningPlansByChild,
    learningPlan: unionPlanIds(learningPlansByChild),
    products: mergeProducts(valid),
    exchanges: mergeExchanges(valid),
    hiddenKnowledgeIds: [...new Set(valid.flatMap((state) => state.hiddenKnowledgeIds ?? []))],
    updatedAt: new Date(Math.max(...valid.map((state) => new Date(state.updatedAt).getTime()))).toISOString()
  });
}

export async function fetchRemoteState() {
  const response = await fetch('/api/state', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('无法读取服务器数据');
  const payload = (await response.json()) as { state: AppState | null };
  return payload.state ? normalizeState(payload.state) : null;
}

export async function loadInitialState() {
  const local = readLocalState();
  try {
    const remote = await fetchRemoteState();
    if (local && remote) {
      const merged = mergeStates([remote, local]);
      writeLocalState(merged);
      void saveRemoteState(merged).catch(() => undefined);
      return merged;
    }
    if (remote) {
      writeLocalState(remote);
      return remote;
    }
    if (local) {
      void saveRemoteState(local).catch(() => undefined);
      return local;
    }
  } catch {
    if (local) return local;
  }
  const seed = createSeedState();
  writeLocalState(seed);
  return seed;
}

export async function saveRemoteState(state: AppState) {
  const response = await fetch('/api/state', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state)
  });
  if (!response.ok) throw new Error('数据同步失败');
  return (await response.json()) as { ok: true; updatedAt: string };
}

export function persistState(state: AppState) {
  writeLocalState(state);
  if (remoteTimer) clearTimeout(remoteTimer);
  remoteTimer = setTimeout(() => {
    void saveRemoteState(state).catch(() => {
      // Local storage remains the offline source of truth.
    });
  }, 250);
}

export async function login(username: string, password: string) {
  try {
    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (response.ok) return (await response.json()) as { ok: true; displayName: string };
    return { ok: false as const, message: '用户名或密码错误' };
  } catch {
    return { ok: false as const, message: '暂时无法连接服务器，请稍后重试' };
  }
}

export async function lookupPoem(title: string): Promise<KnowledgeItem | null> {
  const response = await fetch(`/api/lookup/poem?title=${encodeURIComponent(title)}`);
  if (!response.ok) return null;
  const payload = (await response.json()) as { item: KnowledgeItem | null };
  return payload.item;
}

export async function lookupPoemSong(title: string, author = '', signal?: AbortSignal): Promise<PoemSong | null> {
  const params = new URLSearchParams({ title });
  if (author) params.set('author', author);
  const response = await fetch(`/api/lookup/poem-song?${params.toString()}`, { signal });
  if (!response.ok) return null;
  const payload = (await response.json()) as { song: PoemSong | null };
  return payload.song;
}

export async function lookupWordImages(query: string) {
  const response = await fetch(`/api/lookup/image?q=${encodeURIComponent(query)}`);
  if (!response.ok) return { images: [] as string[], source: 'none' };
  return (await response.json()) as { images: string[]; source: string; fallbackEmoji?: string };
}

export function createBackup(state: AppState) {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `memo-growing-backup-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
}
